/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { Fragment, useState } from "react"
import { useCart } from "frontend/src/utils/useCart"
import { useProducts } from "frontend/src/utils/useProducts"
import { Touchable } from "frontend/src/components/Touchable"
import { Icon } from "frontend/src/components/Icon"
import { couponErrors } from "frontend/src/utils/couponErrors"
import { Flex } from "./Layout"
import Img from "gatsby-image"
import { displayCent } from "frontend/src/utils/displayCent"
import { motion, AnimatePresence } from "framer-motion"
import "@reach/dialog/styles.css"
import { keyframes } from "@emotion/core"
import { useCallback } from "react"
import { FaMinus, FaPlus } from "react-icons/fa"
import { FiChevronDown, FiChevronUp } from "react-icons/fi"

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    rotate(360deg);
  }
`

function Line({ style }) {
  return (
    <div
      sx={{
        mx: [5, 7],
        height: "0px",
        borderTopStyle: "solid",
        borderTopColor: "#ddd",
        borderTopWidth: "1px",
        ...style,
      }}
    />
  )
}

function QuantitySpinner({ value, onChange, onDec, onInc, min = 0, max = 50 }) {
  return (
    <div
      sx={{
        borderColor: "rgba(47,78,37,0.15)",
        borderStyle: "solid",
        borderWidth: "1px",
        display: "inline-block",
        opacity: 1,
        transition: "opacity 300ms ease 0s",
        color: "almostBlack",
      }}
      role="spinbutton"
      aria-valuemax={max}
      aria-valuemin={min}
      aria-valuenow={value}
    >
      <Touchable
        tabIndex={-1}
        sx={{
          pl: "9px",
          py: "9px",
          cursor: "pointer",
          backgroundColor: "transparent",
          border: "medium none",
          fontSize: "12px",
          lineHeight: 1.0,
        }}
        onClick={onDec}
      >
        <FaMinus size="12px" />
      </Touchable>
      <input
        max={max}
        min={min}
        type="number"
        onChange={e => onChange(Number(e.target.value))}
        value={value}
        sx={{
          width: 30,
          height: 30,
          mx: 3,
          WebkitAppearance: "textfield",
          border: "none",
          borderRadius: 6,
          fontSize: 1,
          lineHeight: 1.5,
          color: "almostBlack",
          bg: "transparent",
          p: 1,
          textAlign: "center",
          ":focus": {
            bg: "blue_100",
          },
          "::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
          },
          "::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
          },
        }}
      />
      <Touchable
        tabIndex={-1}
        sx={{
          pr: "9px",
          py: "9px",
          cursor: "pointer",
          backgroundColor: "transparent",
          border: "medium none",
          fontSize: "12px",
          lineHeight: 1.0,
        }}
        onClick={onInc}
      >
        <FaPlus size="12px" />
      </Touchable>
    </div>
  )
}

function DeletableMotionRow(props) {
  return (
    <Flex.row
      positionTransition={true}
      as={motion.div}
      initial={{ backgroundColor: "#ffffff", scaleY: 1, opacity: 1 }}
      animate={{ backgroundColor: "#ffffff", scaleY: 1, opacity: 1 }}
      exit={{ scaleY: 0, opacity: 0, backgroundColor: "#ff0000" }}
      {...props}
    />
  )
}

function ShippingBanner() {
  return (
    <Flex.row
      justify="center"
      align="center"
      sx={{
        px: 4,
        height: "40px",
        bg: "almostBlack",
        color: "white",
        fontSize: ["12px", 0],
        fontFamily: "heading",
        fontWeight: "light",
        lineHeight: "16px",
        letterSpacing: "0.5px",
      }}
    >
      <Flex.row align="center">
        <Icon name="Package" size={20} color="white" />
        <div sx={{ ml: 3 }}>Kostenloser Versand ab 29€</div>
      </Flex.row>
      <div sx={{ mx: 3 }}>|</div>
      <Flex.row align="center">
        <Icon name="Lieferzeit" size={25} color="white" />
        <div sx={{ ml: 3 }}>1 bis 3 Werktage</div>
      </Flex.row>
    </Flex.row>
  )
}

function DecisionBanner() {
  return (
    <Flex.row
      justify="center"
      align="center"
      sx={{
        px: 4,
        height: "40px",
        bg: "#E3FFF1",
        color: "white",
        variant: "text.h5",
        display: ["none", "flex"],
      }}
    >
      <div sx={{ color: "#0A5425" }}>
        <Flex.row align="center">
          <div
            sx={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8ZM12.03 4.97C11.9586 4.89882 11.8735 4.84277 11.7799 4.80522C11.6863 4.76766 11.5861 4.74936 11.4853 4.75141C11.3845 4.75347 11.2851 4.77583 11.1932 4.81717C11.1012 4.85851 11.0185 4.91797 10.95 4.992L7.477 9.417L5.384 7.323C5.24183 7.19052 5.05378 7.1184 4.85948 7.12183C4.66518 7.12525 4.47979 7.20397 4.34238 7.34138C4.20497 7.47879 4.12625 7.66418 4.12283 7.85848C4.1194 8.05278 4.19152 8.24083 4.324 8.383L6.97 11.03C7.04128 11.1012 7.12616 11.1572 7.21958 11.1949C7.313 11.2325 7.41305 11.2509 7.51375 11.2491C7.61444 11.2472 7.71374 11.2251 7.8057 11.184C7.89766 11.1429 7.9804 11.0837 8.049 11.01L12.041 6.02C12.1771 5.8785 12.2523 5.68928 12.2504 5.49296C12.2485 5.29664 12.1698 5.10888 12.031 4.97H12.03Z' fill='%230A5425'/%3E%3C/svg%3E\")",
              width: "16px",
              height: "16px",
            }}
          />
          <div sx={{ fontSize: [1, 2], ml: 2 }}>
            <span sx={{ fontWeight: "bold" }}>Gute Wahl!</span> Hier sind Ihre Produkte:
          </div>
        </Flex.row>
      </div>
    </Flex.row>
  )
}

function ClosedCart() {
  const { toggle, prices } = useCart()
  const trackCartOpen = useCallback(() => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: "cartOpen",
        price: prices.finalPrice,
        section: "cart",
      })
    }
  }, [prices])
  return (
    <Touchable
      sx={{ width: "100%" }}
      onClick={() => {
        toggle()
        trackCartOpen()
      }}
    >
      <Flex.row>
        <Flex
          align="center"
          justify="center"
          sx={{ py: 3, px: 5, borderColor: "almostBlack", border: "2px solid" }}
        >
          <div sx={{ variant: "text.h5" }}>{displayCent(prices.finalPrice)}</div>
          <div sx={{ variant: "text.h5", fontSize: 0 }}>inkl. MwSt</div>
        </Flex>
        <Flex
          align="center"
          justify="center"
          sx={{
            variant: "text.h5",
            textAlign: "center",
            flex: 1,
            bg: "almostBlack",
            color: "white",
          }}
        >
          Zum Warenkorb
        </Flex>
      </Flex.row>
    </Touchable>
  )
}

function SubscriptionPicker({
  enabled,
  currentID,
  allSubscriptions,
  onChange,
  subPercent,
  lineItemID,
}) {
  const [showTerms, setShowTerms] = useState(false)

  return (
    <Fragment>
      <AnimatePresence>
        {showTerms && (
          <Flex
            as={motion.div}
            key="bg"
            intial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            sx={{
              zIndex: 11000,
              position: "fixed",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              bg: "rgba(0,0,0,0.2)",
            }}
          >
            <Touchable onClick={() => setShowTerms(false)} sx={{ width: "100%", height: "100%" }} />
          </Flex>
        )}
        {showTerms && (
          <Flex
            key="terms"
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            as={motion.div}
            align="center"
            sx={{
              zIndex: 12000,
              position: "fixed",
              top: ["24px", 100],
              left: ["12px", 50],
              right: ["12px", 50],
              bg: "white",
              p: 5,
              fontFamily: "body",
              borderRadius: 8,
              boxShadow: "control",
            }}
          >
            <Touchable onClick={() => setShowTerms(false)} sx={{ alignSelf: "flex-end" }}>
              <Icon name="Close" size={12} color="almostBlack" />
            </Touchable>
            <Icon name="Savings" size={100} sx={{ m: 8 }} color="almostBlack" />
            <h2 sx={{ variant: "text.h4", textAlign: "center" }}>Spar-Abo</h2>
            <ol>
              <li>Produkte für regelmäßige Lieferung auswählen</li>
              <li>Häkchen setzen</li>
              <li>Lieferintervall auswähen</li>
              <li>Wie gewohnt bestellen... fertig!</li>
            </ol>
            <h3 sx={{ variant: "text.h4", textAlign: "center" }}>Bequem, Flexibel, Risikofrei!</h3>
            <ul>
              <li>11-16% Rabatt (für diese und alle Folgebestellungen des Abos)</li>
              <li>Keine Mindestvertragslaufzeit</li>
              <li>Jederzeit kündbar</li>
            </ul>
          </Flex>
        )}
      </AnimatePresence>
      <form>
        <Flex.row align="center" sx={{ mt: 0, mb: 2, opacity: enabled ? 0.6 : 1 }}>
          <input
            checked={!enabled}
            onChange={() => onChange(lineItemID, null)}
            type="radio"
            id={`radio-nosub-${lineItemID}`}
            name={`subscription-${lineItemID}`}
            sx={{ mr: 3 }}
          />
          <label
            sx={{
              fontFamily: "heading",
              fontSize: ["13px", "15px"],
              lineHeight: ["16px", "19px"],
              letterSpacing: "0px",
              fontWeight: "light",
            }}
            htmlFor={`radio-nosub-${lineItemID}`}
          >
            Einmalige Lieferung
          </label>
        </Flex.row>
        <Flex.row align="center" sx={{ mt: 0, mb: 2, opacity: enabled ? 1 : 0.6 }}>
          <input
            checked={enabled}
            onChange={() => onChange(lineItemID, allSubscriptions[0].ourID)}
            type="radio"
            id={`radio-yessub-${lineItemID}`}
            name={`subscription-${lineItemID}`}
            sx={{ mr: 3, flexShrink: 0 }}
          />
          <Flex.row
            align="center"
            sx={{
              fontFamily: "heading",
              fontSize: ["13px", "15px"],
              lineHeight: ["16px", "19px"],
              letterSpacing: "0px",
              fontWeight: "light",
            }}
          >
            <label htmlFor={`radio-yessub-${lineItemID}`}>Spar Abo</label>
            <span sx={{ mx: 2, "@media (max-width: 340px)": { display: "none" } }}>-</span>
            <span
              sx={{
                color: "#B53838",
                "@media (max-width: 340px)": { display: "none" },
              }}
            >{`${subPercent}% Rabatt`}</span>
            <Touchable sx={{ mx: 2, mb: -2 }} onClick={() => setShowTerms(true)}>
              <Icon name="Info" color="#555" size={12} />
            </Touchable>
          </Flex.row>
        </Flex.row>
      </form>
      <Flex.row
        align="center"
        sx={{
          ml: 7,
          mt: 2,
          position: "relative",
          opacity: enabled ? 1 : 0.5,
          width: 140,
        }}
      >
        <select
          disabled={!enabled}
          value={currentID}
          onChange={e => {
            onChange(lineItemID, e.target.value)
          }}
          sx={{
            py: 3,
            pl: 6,
            bg: "white",
            width: "100%",
            color: "#333",
            fontSize: "12px",
            borderColor: "rgba(47,78,37,0.15)",
            borderStyle: "solid",
            borderWidth: "1px",
            MozAppearance: "none",
            WebkitAppearance: "none",
            appearance: "none",
            "::-ms-expand": { display: "none" },
          }}
        >
          {allSubscriptions.map(sub => (
            <option value={sub.ourID} key={sub.ourID}>
              {sub.cartName}
            </option>
          ))}
        </select>
        <Icon name="Clock" color="#888" size={8} sx={{ position: "absolute", mx: 3 }} />
        <Icon
          name="Dropdown"
          color="#888"
          size={8}
          sx={{ position: "absolute", mx: 3, right: 0 }}
        />
      </Flex.row>
    </Fragment>
  )
}

function CouponInput({
  value,
  onChange,
  onConfirm,
  onReset,
  isValid,
  isLoading,
  className = "",
  couponError = undefined,
  coupon = undefined,
  saving = undefined,
}) {
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = () => {
    setExpanded(!expanded)
  }
  return (
    <div>
      {expanded ? (
        <Fragment>
          <Flex.row justify="space-between" sx={{ width: "100%", px: [5, 7] }}>
            <Flex.column sx={{ width: "calc(100% - 20px)" }}>
              <Flex.row
                as={"form"}
                align="center"
                sx={{
                  px: "0px !important",
                  pb: "0px !important",
                  position: "relative",
                  width: "calc(100% - 20px)",
                }}
                className={className}
              >
                <input
                  type="text"
                  onChange={e => onChange(e.target.value)}
                  placeholder="Gutscheincode einlösen (optional)"
                  autoCapitalize="none"
                  value={value || ""}
                  onFocus={e => (e.target.placeholder = "")}
                  onBlur={e => (e.target.placeholder = "Gutscheincode einlösen (optional)")}
                  sx={{
                    width: "100%",
                    WebkitAppearance: "none",
                    px: [3, 4],
                    py: [3, 4],
                    borderColor: "rgba(47,78,37,0.15)",
                    borderStyle: "solid",
                    borderWidth: "1px",
                    fontSize: 1,
                    fontWeight: "light",
                    color: isValid ? "green_300" : "almostBlack",
                    ":placeholder": {
                      color: "almostBlack",
                    },
                  }}
                />
                {value && (
                  <Touchable
                    onMouseDown={e => {
                      e.preventDefault()
                      onReset()
                    }}
                    sx={{
                      pointerEvents: value ? "auto" : "none",
                      position: "absolute",
                      right: 6,
                      mx: 5,
                    }}
                  >
                    <Icon name="Clear" size={10} />
                  </Touchable>
                )}
                {value && (
                  <Touchable
                    role="button"
                    type="submit"
                    onMouseDown={e => {
                      e.preventDefault()
                      onConfirm()
                    }}
                    onClick={() => onConfirm()}
                    sx={{
                      position: "absolute",
                      right: 0,
                      mx: 5,
                      transform: "rotate(270deg)",
                      animation: isLoading ? `${spin} 0.7s linear infinite` : "",
                    }}
                  >
                    <Icon name={isLoading ? "Spinner" : "Dropdown"} size={10} />
                  </Touchable>
                )}
              </Flex.row>
              {(couponError || (coupon && saving === 0)) && (
                <Flex.row
                  sx={{
                    fontSize: ["12px", 1],
                    fontFamily: "heading",
                    fontWeight: "light",
                    lineHeight: "16px",
                    letterSpacing: "0px",
                    mt: 3,
                    mx: 0,
                    color: "#c85050",
                  }}
                >
                  {couponError
                    ? couponErrors[couponError] || couponErrors.FALLBACK
                    : couponErrors.MINIMUM + displayCent(coupon.couponMinAmount)}
                </Flex.row>
              )}
            </Flex.column>
            <button
              sx={{
                background: "transparent",
                border: "none",
                width: "20px",
                position: "relative",
                right: "8px",
                top: "8px",
              }}
              onClick={toggleExpanded}
            >
              <FiChevronUp />
            </button>
          </Flex.row>
          <Line style={{ mt: 5 }} />
        </Fragment>
      ) : (
        <Fragment>
          <Flex.row justify="space-between" sx={{ width: "100%", px: [5, 7], py: 3 }}>
            <div
              sx={{
                width: "calc(100% - 20px)",
                textAlign: "left",
                fontFamily: "heading",
                fontSize: 1,
                fontWeight: "regular",
              }}
              onClick={toggleExpanded}
            >
              Gutscheincode einlösen
            </div>
            <button
              onClick={toggleExpanded}
              sx={{ background: "transparent", border: "none", width: "20px", mr: 3 }}
            >
              <FiChevronDown />
            </button>
          </Flex.row>
          <Line />
        </Fragment>
      )}
    </div>
  )
}

function Cart() {
  const {
    lineItems,
    incrementQuantity,
    decrementQuantity,
    changeQuantity,
    changeSubscription,
    remove,
    prices,
    checkoutURL,
    coupon,
    couponError,
    tryCoupon,
    toggle,
    numberOfItems,
  } = useCart()
  const products = useProducts()
  const [enteredCoupon, setEnteredCoupon] = useState(coupon?.couponName)
  const [couponLoading, setCouponLoading] = useState(false)
  const { preDiscountTotal, shipping, saving, finalPrice } = prices

  const trackCartClose = useCallback(() => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: "cartClose",
        price: prices.finalPrice,
        section: "cart",
      })
    }
  }, [prices])

  const trackProductRemove = useCallback(
    id => {
      if (typeof window !== `undefined`) {
        window.dataLayer.push({
          event: "productRemoved",
          price: prices.finalPrice,
          product: id,
        })
      }
    },
    [prices]
  )
  const trackQtyChange = useCallback(
    ({ variant, qty }) => {
      if (typeof window !== `undefined`) {
        window.dataLayer.push({
          event: "productQtyChanged",
          price: prices.finalPrice,
          product: variant,
          increaseDecrease: qty,
        })
      }
    },
    [prices]
  )
  const trackCouponAdd = useCallback(coupon => {
    if (typeof window !== `undefined`) {
      window.dataLayer.push({
        event: "couponAdded",
        code: coupon,
      })
    }
  }, [])
  const trackCheckout = useCallback(
    url => {
      if (typeof window !== `undefined`) {
        window.dataLayer.push({
          event: "checkoutInitiated",
          price: prices.finalPrice,
          checkoutURL: url,
        })
      }
    },
    [prices]
  )
  return (
    <Flex sx={{ flex: 1, maxHeight: "100%" }}>
      <ShippingBanner />
      <Flex.row justify="space-between" align="center" sx={{ borderBottom: "none" }}>
        <div sx={{ width: 11, mx: [7, 8] }} />
        <div
          sx={{
            fontFamily: "heading",
            fontSize: [2, 3],
            lineHeight: 1.3,
            letterSpacing: "0px",
            fontWeight: "medium",
            color: "almostBlack",
            my: [3, 5],
          }}
        >
          WARENKORB
        </div>
        <Touchable
          sx={{ width: 11, mx: [7, 8], position: "relative", bottom: "4px" }}
          onClick={() => {
            toggle()
            trackCartClose()
          }}
        >
          <Icon name="Close" size={11} color="almostBlack" />
        </Touchable>
      </Flex.row>
      <Line style={{ display: ["block", "none"] }} />

      <Flex
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "none",
          WebkitOverflowScrolling: "touch",
          pb: 5,
        }}
      >
        {lineItems.length > 0 && <DecisionBanner />}
        <AnimatePresence>
          {lineItems.map(
            (
              { variantID, productID, quantity, key, subscription, subscriptionID, price },
              index,
              arr
            ) => {
              const product = products[productID]
              const activeVariant = product.variants.find(({ ourID }) => ourID === variantID)
              const normPrice = activeVariant.price
              const activeSubscription =
                activeVariant.subscriptions.length > 0
                  ? subscription
                    ? activeVariant.subscriptions.find(({ ourID }) => ourID === subscriptionID)
                    : activeVariant.subscriptions[0]
                  : activeVariant
              const subPrice = activeSubscription.price
              const subPercent = Math.round(((normPrice - subPrice) / normPrice) * 100)
              return (
                <Fragment>
                  <DeletableMotionRow
                    key={key}
                    align="stretch"
                    sx={{ maxWidth: "100vw", overflow: "hidden" }}
                  >
                    <Flex
                      align="center"
                      sx={{ ml: [5, 7], mr: [4, 5], mb: 5, "@media (max-width: 340px)": { mx: 3 } }}
                    >
                      <Img
                        sx={{ objectFit: "contain", mb: [3, 5], mt: [3, 8] }}
                        style={{ width: 60, height: 60 }}
                        imgStyle={{ objectFit: "contain" }}
                        fixed={activeVariant.image.asset.fixed}
                      />
                      <QuantitySpinner
                        value={quantity}
                        onInc={() => {
                          incrementQuantity(key)
                          trackQtyChange({
                            variant: activeVariant.ourID,
                            qty: 1,
                          })
                        }}
                        onDec={() => {
                          decrementQuantity(key)
                          trackQtyChange({
                            variant: activeVariant.ourID,
                            qty: -1,
                          })
                        }}
                        onChange={value => {
                          changeQuantity(key, value)
                          trackQtyChange({ variant: activeVariant.ourID, qty: value })
                        }}
                      />
                    </Flex>
                    <Flex.row
                      sx={{ flex: 1, my: 5, mr: [5, 7], "@media (max-width: 340px)": { mr: 3 } }}
                    >
                      <Flex.column sx={{ flex: 1 }}>
                        <h3
                          sx={{
                            fontFamily: "heading",
                            fontSize: ["15px", 2],
                            lineHeight: [1.2, 1.33],
                            fontWeight: "regular",
                            letterSpacing: "0px",
                            mb: 2,
                          }}
                        >
                          {activeVariant.name}
                        </h3>
                        <h4
                          sx={{
                            fontFamily: "heading",
                            fontSize: [0, 1],
                            lineHeight: ["18px", 1.25],
                            mb: 4,
                            fontWeight: "light",
                            letterSpacing: "0px",
                          }}
                        >
                          {activeVariant.cartName}
                        </h4>
                        {activeVariant.subscriptions.length > 0 && (
                          <SubscriptionPicker
                            enabled={subscription}
                            currentID={subscriptionID}
                            allSubscriptions={activeVariant.subscriptions}
                            onChange={changeSubscription}
                            subPercent={subPercent}
                            lineItemID={key}
                          />
                        )}
                      </Flex.column>
                      <Flex.column align="flex-end">
                        <Touchable
                          onClick={() => {
                            remove(key)
                            trackProductRemove(activeVariant.ourID)
                          }}
                        >
                          <Icon name="Trash" size={16} color="almostBlack" />
                        </Touchable>
                        <div
                          sx={{
                            fontSize: ["15px", 1],
                            fontFamily: "body",
                            fontWeight: "regular",
                            lineHeight: [1.0, "26px"],
                            letterSpacing: "0px",
                            mt: ["-4px", "-2px"],
                            mb: 5,
                            pt: 3,
                          }}
                        >
                          {displayCent(price)}
                        </div>
                      </Flex.column>
                    </Flex.row>
                  </DeletableMotionRow>
                  {index !== arr.length - 1 && <Line />}
                </Fragment>
              )
            }
          )}
        </AnimatePresence>
      </Flex>
      <Line />
      <Flex sx={{ alignItems: "stretch" }}>
        <CouponInput
          value={enteredCoupon}
          isLoading={couponLoading}
          isValid={couponError === ""}
          onChange={couponName => setEnteredCoupon(couponName)}
          onConfirm={() => {
            setCouponLoading(true)
            tryCoupon(enteredCoupon).then(() => setCouponLoading(false))
            trackCouponAdd(enteredCoupon)
          }}
          onReset={() => {
            tryCoupon("")
            setEnteredCoupon("")
          }}
          saving={saving}
          coupon={coupon}
          couponError={couponError}
          sx={{ padding: 5 }}
        />
        <Flex sx={{ px: [5, 7], pt: [5, 7], pb: [7, 8] }}>
          <div
            sx={{
              fontSize: ["18px", "21px"],
              fontFamily: "heading",
              fontWeight: "semiBold",
              lineHeight: ["24px", "28px"],
              letterSpacing: "0px",
              pt: 0,
              pb: 3,
            }}
          >
            Bestellübersicht
          </div>
          <Flex.row
            justify="space-between"
            sx={{
              pb: 3,
            }}
          >
            <div
              sx={{
                fontSize: [1, 2],
                fontFamily: "body",
                fontWeight: "light",
                lineHeight: ["18px", "24px"],
                letterSpacing: "0px",
              }}
            >{`${numberOfItems} Artikel`}</div>
            <div
              sx={{
                fontSize: [1, 2],
                fontFamily: "body",
                fontWeight: "light",
                lineHeight: ["18px", "24px"],
                letterSpacing: "0px",
              }}
            >
              {displayCent(preDiscountTotal)}
            </div>
          </Flex.row>
          {saving > 0 && (
            <Flex.row justify="space-between" sx={{ pb: 3, color: "green_300" }}>
              <div
                sx={{
                  fontSize: [1, 2],
                  fontFamily: "body",
                  fontWeight: "light",
                  lineHeight: ["18px", "24px"],
                  letterSpacing: "0px",
                }}
              >
                Gutscheincode: {coupon?.couponName}
              </div>
              <div
                sx={{
                  fontSize: [1, 2],
                  fontFamily: "body",
                  fontWeight: "light",
                  lineHeight: ["18px", "24px"],
                  letterSpacing: "0px",
                }}
              >
                -{displayCent(saving)}
              </div>
            </Flex.row>
          )}
          <Flex.row justify="space-between" sx={{ pb: 3 }}>
            <div
              sx={{
                fontSize: [1, 2],
                fontFamily: "body",
                fontWeight: "light",
                lineHeight: ["18px", "24px"],
                letterSpacing: "0px",
              }}
            >
              Versand
            </div>
            <div
              sx={{
                fontSize: [1, 2],
                fontFamily: "body",
                fontWeight: "light",
                lineHeight: ["18px", "24px"],
                letterSpacing: "0px",
              }}
            >
              {lineItems.length > 0 ? displayCent(shipping) : displayCent(0)}
            </div>
          </Flex.row>
          <Line style={{ mx: "0px !important" }} />
          <Flex.row justify="space-between" sx={{ py: 4 }}>
            <Flex.row align="baseline">
              <div
                sx={{
                  fontSize: [2],
                  fontFamily: "heading",
                  fontWeight: "medium",
                  lineHeight: "28px",
                  letterSpacing: "0px",
                }}
              >
                Gesamtbetrag
              </div>
              <div
                sx={{
                  fontFamily: "heading",
                  lineHeight: "26px",
                  letterSpacing: "0px",
                  fontWeight: "light",
                  ml: 1,
                  fontSize: 0,
                }}
              >
                inkl. MwSt
              </div>
            </Flex.row>
            <div
              sx={{
                fontSize: [1, 2],
                fontFamily: "body",
                fontWeight: "medium",
                lineHeight: ["18px", "26px"],
                letterSpacing: "0px",
              }}
            >
              {lineItems.length > 0 ? displayCent(finalPrice) : displayCent(0)}
            </div>
          </Flex.row>
          <Touchable
            onClick={() => {
              trackCheckout(checkoutURL)
            }}
            href={checkoutURL}
            sx={{
              bg: "almostBlack",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon name="Lock" color="white" size={20} sx={{ mb: 2 }} />
            <div sx={{ color: "white", py: 5, mx: 3, variant: "text.h5" }}>Zur Kasse</div>
          </Touchable>
        </Flex>
      </Flex>
    </Flex>
  )
}

export { Cart, ClosedCart }
